import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, Plus, Trash2, User } from "lucide-react";
import logo from '../../assets/images/film-artie.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOnboarding } from "@/context/OnboardingContext";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";

export default function OnboardingV2Page3_2() {
    const navigate = useNavigate();
    const { data, updateData } = useOnboarding();
    const { user, checkAuth } = useAuthStore();

    useEffect(() => {
        if (!data.scriptFile && !data.scriptId) {
            navigate('/onboarding-v2');
        }
    }, [data.scriptFile, data.scriptId, navigate]);

    // Section 2: Film/TV/Festivals State
    const [imdbProfile, setImdbProfile] = useState("");
    const [projectRoles, setProjectRoles] = useState<string[]>([]);
    const [collaborators, setCollaborators] = useState<{ name: string; role: string[]; imdbProfile: string }[]>([]);

    // New Checkbox
    const [noImdb, setNoImdb] = useState(false);

    // Initialize state from context first, then user data
    useEffect(() => {
        // 1. Try to load from context (data) first
        if (data.imdbProfile) setImdbProfile(data.imdbProfile);
        if (data.projectRole && data.projectRole.length > 0) setProjectRoles(data.projectRole);
        if (data.collaborators && data.collaborators.length > 0) setCollaborators(data.collaborators);

        // 2. If context is empty, fall back to user data (backend)
        // We only do this if the corresponding context fields are empty to avoid overwriting user edits
        if (user) {
            // IMDb Profile
            if (!data.imdbProfile) {
                const imdb = user.social_profiles?.find(p => p.platform === "imdb");
                const userImdb = imdb?.url || user.imdb_profile || "";
                if (userImdb) setImdbProfile(userImdb);
            }

            // Collaborators
            if (!data.collaborators || data.collaborators.length === 0) {
                if (user.collaborators && user.collaborators.length > 0) {
                    setCollaborators(user.collaborators.map(c => ({
                        name: c.name,
                        role: Array.isArray(c.role) ? c.role : [c.role],
                        imdbProfile: c.imdb_profile || ""
                    })));
                }
            }

            // Project Roles
            if (!data.projectRole || data.projectRole.length === 0) {
                const standardRoles = ["Executive Producer", "Producer", "Director", "Writer"];
                if (user.project_role) {
                    // Handle both string and array for backward compatibility
                    const userRoles = Array.isArray(user.project_role) ? user.project_role : [user.project_role];
                    const initRoles: string[] = [];

                    userRoles.forEach(r => {
                        if (standardRoles.includes(r)) {
                            initRoles.push(r);
                        } else if (r === "Other" || !standardRoles.includes(r)) {
                            // If it's "Other" or a legacy custom role, map to "Other"
                            if (!initRoles.includes("Other")) {
                                initRoles.push("Other");
                            }
                        }
                    });
                    
                    if (initRoles.length > 0) setProjectRoles(initRoles);
                }
            }
        }
    }, [data, user]);

    // Collaborator Input State
    const [collabName, setCollabName] = useState("");
    const [collabRoles, setCollabRoles] = useState<string[]>([]);
    const [collabImdb, setCollabImdb] = useState("");

    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const validateUrl = (url: string) => {
        if (!url) return true;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };


    const toggleRole = (role: string) => {
        setProjectRoles(prev => {
            if (prev.includes(role)) {
                return prev.filter(r => r !== role);
            } else {
                return [...prev, role];
            }
        });
    };

    const toggleCollabRole = (role: string) => {
        setCollabRoles(prev => {
            if (prev.includes(role)) {
                return prev.filter(r => r !== role);
            } else {
                return [...prev, role];
            }
        });
    };

    const handleAddCollaborator = () => {
        if (!collabName || collabRoles.length === 0) {
            toast.error("Name and at least one Role are required for a collaborator.");
            return;
        }

        if (collabImdb && !validateUrl(collabImdb)) {
            setErrors(prev => ({ ...prev, collabImdb: "Please enter a valid URL (e.g., https://...)" }));
            return;
        }

        setCollaborators([...collaborators, { name: collabName, role: collabRoles, imdbProfile: collabImdb }]);
        setCollabName("");
        setCollabRoles([]);
        setCollabImdb("");
        setErrors(prev => ({ ...prev, collabImdb: undefined }));
    };

    const handleRemoveCollaborator = (index: number) => {
        const newCollaborators = [...collaborators];
        newCollaborators.splice(index, 1);
        setCollaborators(newCollaborators);
    };

    const handleNext = async () => {
        const newErrors: Record<string, string> = {};
        if (imdbProfile && !validateUrl(imdbProfile)) newErrors.imdbProfile = "Please enter a valid URL";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error("Please fix the validation errors before continuing");
            return;
        }

        try {
            setIsSaving(true);

            // Save data to context
            updateData({
                projectRole: projectRoles,
                collaborators
            });

            // Prepare collaborators for backend (map imdbProfile to imdb_profile)
            const backendCollaborators = collaborators.map(c => ({
                name: c.name,
                role: c.role,
                imdb_profile: c.imdbProfile
            }));

            // Construct update object
            const socialProfiles = user?.social_profiles || [];
            if (imdbProfile) {
                const handle = imdbProfile.split("/").filter(Boolean).slice(-1)[0] || user?.name || "IMDb User";
                const imdbProfileData = {
                    platform: "imdb" as const,
                    handle: handle,
                    url: imdbProfile,
                };
                // Remove existing imdb profile if it exists
                const existingImdbIndex = socialProfiles.findIndex(p => p.platform === "imdb");
                if (existingImdbIndex > -1) {
                    socialProfiles[existingImdbIndex] = imdbProfileData;
                } else {
                    socialProfiles.push(imdbProfileData);
                }
            }

            const updatePayload: any = {
                project_role: projectRoles,
                collaborators: backendCollaborators,
                social_profiles: socialProfiles,
            };

            await apiClient.updateCurrentUser(updatePayload);
            await checkAuth(); // Refresh user data

            navigate('/onboarding-v2/page4');
        } catch (error) {
            console.error("Error saving data:", error);
            toast.error("Failed to save data. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    // Validation Logic
    const hasImdb = imdbProfile && validateUrl(imdbProfile);
    const canProceed = hasImdb || noImdb;

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">

            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
                <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header Section (Same as Page 3) */}
                    <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            Help me understand <br />your audience
                        </h1>
                        <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2">
                            Everyone has an audience for their work - some have an established following on digital platforms, while others are known through film, TV, or festival circuits. Either way, share what you can - audience reach is the foundation for connecting with brand partners.
                            Don't have much of an audience yet? That's okay. We'll help you grow your reach and identify who will resonate with your project.
                        </p>
                    </div>

                    {/* Main Form Container */}
                    <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden animate-in slide-in-from-bottom-4 duration-500 delay-100 p-8 md:p-10">
                        <div className="space-y-10">

                            {/* Section 2: Film/TV/Festivals */}
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <label className="text-xl font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                        Are you known from film, TV, or festivals?
                                    </label>
                                    <p className="text-sm text-[oklch(0.55_0_0)] leading-tight">
                                        This helps me understand your track record and connect you with the right opportunities.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {/* IMDb Profile */}
                                    <div>
                                        <label className="text-sm font-medium text-[oklch(0.2686_0_0)] mb-1.5 block">
                                            Your IMDb profile
                                        </label>
                                        <div className="relative">
                                            <Input
                                                placeholder="https://www.imdb.com/name/nm..."
                                                value={imdbProfile}
                                                onChange={(e) => {
                                                    setImdbProfile(e.target.value);
                                                    if (errors.imdbProfile) setErrors(prev => ({ ...prev, imdbProfile: undefined }));
                                                    if (e.target.value) setNoImdb(false);
                                                }}
                                                className={`bg-gray-50 border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base pl-20 rounded-lg w-full ${errors.imdbProfile ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                                            />
                                            {errors.imdbProfile && <p className="text-red-500 text-xs mt-1 ml-1">{errors.imdbProfile}</p>}
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                                <img
                                                    src="https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg"
                                                    alt="IMDb"
                                                    className="w-12 h-auto"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Project Role */}
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium text-[oklch(0.2686_0_0)] block">
                                            What's your role on this project?
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {["Executive Producer", "Producer", "Director", "Writer", "Other"].map((role) => (
                                                <button
                                                    key={role}
                                                    onClick={() => toggleRole(role)}
                                                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${projectRoles.includes(role)
                                                        ? "bg-[oklch(0.2686_0_0)] text-white border-[oklch(0.2686_0_0)]"
                                                        : "bg-white text-[oklch(0.55_0_0)] border-[oklch(0.92_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                        }`}
                                                >
                                                    {role}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Collaborators */}
                                    <div className="pt-2">
                                        <div className="mb-3">
                                            <label className="text-sm font-medium text-[oklch(0.2686_0_0)] block">
                                                Is anyone else attached?
                                            </label>
                                            <p className="text-xs text-[oklch(0.55_0_0)]">
                                                Add collaborators so I can factor in the full team's reach and credibility.
                                            </p>
                                        </div>

                                        {/* Added Collaborators List */}
                                        {collaborators.length > 0 && (
                                            <div className="mb-4 space-y-2">
                                                {collaborators.map((collab, index) => (
                                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-[oklch(0.92_0_0)]">
                                                        <div className="flex items-center gap-3 overflow-hidden">
                                                            <div className="w-8 h-8 rounded-full bg-[oklch(0.96_0_0)] flex items-center justify-center flex-shrink-0">
                                                                <User className="w-4 h-4 text-[oklch(0.55_0_0)]" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-[oklch(0.2686_0_0)] truncate">{collab.name}</p>
                                                                <p className="text-xs text-[oklch(0.55_0_0)] truncate">{Array.isArray(collab.role) ? collab.role.join(", ") : collab.role} {collab.imdbProfile && "• IMDb Linked"}</p>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleRemoveCollaborator(index)}
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Add Collaborator Form */}
                                        <div className="bg-gray-50 p-4 rounded-xl border border-dashed border-[oklch(0.85_0_0)] space-y-3">
                                            <div className="space-y-3">
                                                <Input
                                                    placeholder="Name"
                                                    value={collabName}
                                                    onChange={(e) => setCollabName(e.target.value)}
                                                    className="bg-white h-10 text-sm"
                                                />

                                                <div className="flex flex-wrap gap-2">
                                                    {["Executive Producer", "Producer", "Director", "Writer", "Other"].map((role) => (
                                                        <button
                                                            key={role}
                                                            onClick={() => toggleCollabRole(role)}
                                                            className={`px-4 py-2 rounded-full text-xs font-medium transition-all border ${collabRoles.includes(role)
                                                                ? "bg-[oklch(0.2686_0_0)] text-white border-[oklch(0.2686_0_0)]"
                                                                : "bg-white text-[oklch(0.55_0_0)] border-[oklch(0.92_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                                }`}
                                                        >
                                                            {role}
                                                        </button>
                                                    ))}
                                                </div>

                                                <div>
                                                    <Input
                                                        placeholder="IMDb Link (Optional)"
                                                        value={collabImdb}
                                                        onChange={(e) => {
                                                            setCollabImdb(e.target.value);
                                                            if (errors.collabImdb) setErrors(prev => ({ ...prev, collabImdb: undefined }));
                                                        }}
                                                        className={`bg-white h-10 text-sm ${errors.collabImdb ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                                                    />
                                                    {errors.collabImdb && <p className="text-red-500 text-xs mt-1 ml-1">{errors.collabImdb}</p>}
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleAddCollaborator}
                                                className="w-full border-dashed border-[oklch(0.70_0.18_55)] text-[oklch(0.2686_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                disabled={!collabName || collabRoles.length === 0}
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Collaborator
                                            </Button>
                                        </div>
                                    </div>

                                    {/* New "I don't have an IMDb profile" Checkbox */}
                                    <div className="flex items-center space-x-3 p-4 border border-[oklch(0.92_0_0)] rounded-xl bg-gray-50 mt-4">
                                        <Checkbox
                                            id="no-imdb"
                                            checked={noImdb}
                                            onCheckedChange={(checked) => {
                                                const isChecked = checked as boolean;
                                                setNoImdb(isChecked);
                                                if (isChecked) {
                                                    setImdbProfile("");
                                                    setProjectRoles([]);
                                                    setCollaborators([]);
                                                    setCollabName("");
                                                    setCollabRoles([]);
                                                    setCollabImdb("");
                                                    setErrors({});
                                                }
                                            }}
                                            className="data-[state=checked]:bg-[oklch(0.2686_0_0)] data-[state=checked]:border-[oklch(0.2686_0_0)] data-[state=checked]:text-white"
                                        />
                                        <label
                                            htmlFor="no-imdb"
                                            className="text-base font-medium text-[oklch(0.2686_0_0)] cursor-pointer flex-1"
                                        >
                                            I'm early and don't have an IMDb profile yet
                                        </label>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </Card>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-6 md:px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={() => {
                                    // Save current state to context before navigating back
                                    updateData({
                                        imdbProfile,
                                        projectRole: projectRoles,
                                        collaborators
                                    });
                                    navigate('/onboarding-v2/page3', { state: { keepData: true } });
                                }}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </div>

                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={isSaving || !canProceed}
                            >
                                {isSaving ? (
                                    <>
                                        Saving... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        Continue
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}